import { useQuery } from '@tanstack/react-query';
import {
  ACCESS_LEVEL_LABELS,
  type BackupsResponse,
  modulesVisibleTo,
  type Permission,
} from '@k-management/shared';
import { Link } from 'react-router';
import { useCurrentUser } from '../auth/AuthProvider.js';
import { BackupHealthNotice } from '../components/BackupHealthNotice.js';
import { apiRequest } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';

/** Landing page: the modules the user may open; administrators also see the backup status. */
export function HomePage() {
  const user = useCurrentUser();
  const modules = modulesVisibleTo(user.permissions);
  const backups = useQuery({
    queryKey: queryKeys.backups,
    queryFn: () => apiRequest<BackupsResponse>('/backups'),
    enabled: user.isAdmin,
  });

  return (
    <section className="stack">
      <div className="page-header">
        <h1>Ciao {user.firstName}</h1>
      </div>
      {backups.data && backups.data.health.state !== 'ok' && (
        <BackupHealthNotice health={backups.data.health} link />
      )}
      {modules.length === 0 ? (
        <p className="notice info">
          Non hai ancora accesso a nessun modulo: chiedi all’amministratore di abilitarti.
        </p>
      ) : (
        <div className="module-grid">
          {modules.map((module) => (
            <Link key={module.key} to={module.path} className="card module-card">
              <h2>{module.label}</h2>
              <p className="muted">{module.description}</p>
              <small>{levelLabel(user.permissions, module.key)}</small>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function levelLabel(permissions: readonly Permission[], module: string): string {
  const granted = permissions as readonly string[];
  if (granted.includes(`${module}.admin`)) return ACCESS_LEVEL_LABELS.admin;
  if (granted.includes(`${module}.write`)) return ACCESS_LEVEL_LABELS.write;
  return ACCESS_LEVEL_LABELS.read;
}
